import type { SettingsRow } from '@bn-games/ui-shell';
import type { PointGoal, Settings } from '../settings/types.js';
import { getRuleSet } from '../game/variants/index.js';
import { t } from '../i18n/I18n.js';

const POINT_GOALS: readonly PointGoal[] = [100, 150, 250];

export interface RuleRowsInput {
  readonly settings: Settings;
  /** Game-config change — the pause menu wraps it with confirm + restart. */
  readonly applyGameConfig: (partial: Partial<Settings>) => void;
  /** Plain settings write for UI-only preferences. */
  readonly updateSettings: (partial: Partial<Settings>) => void;
  readonly disableGameConfig: boolean;
}

function isPenaltyVariant(settings: Settings): boolean {
  return getRuleSet(settings.variant).scoring === 'penalty';
}

/**
 * Match-scoring rows for the selected variant. Козёл plays a fixed 101 match,
 * so the target-score selector would be a dead control there; its two
 * optional rules take that slot instead.
 */
export function scoringRuleRows(input: RuleRowsInput): readonly SettingsRow[] {
  const { settings: s, applyGameConfig, disableGameConfig } = input;
  if (isPenaltyVariant(s)) {
    return [
      {
        kind: 'toggle',
        key: 'goatOpening13',
        label: t('settings.rows.goatOpening13'),
        value: s.goatOpening13,
        onClick: () => applyGameConfig({ goatOpening13: !s.goatOpening13 }),
        disabled: disableGameConfig,
        tab: 'gameplay',
      },
      {
        kind: 'toggle',
        key: 'goatBaldFinish',
        label: t('settings.rows.goatBaldFinish'),
        value: s.goatBaldFinish,
        onClick: () => applyGameConfig({ goatBaldFinish: !s.goatBaldFinish }),
        disabled: disableGameConfig,
        tab: 'gameplay',
      },
    ];
  }
  return [
    {
      kind: 'selector',
      key: 'pointGoal',
      label: t('settings.rows.pointGoal'),
      value: String(s.pointGoal),
      options: POINT_GOALS.map((v) => String(v)),
      format: (v) => v,
      onChange: (next) => applyGameConfig({ pointGoal: Number(next) as PointGoal }),
      disabled: disableGameConfig,
      tab: 'gameplay',
    },
  ];
}

/** Muggins rows — hidden for Козёл, which has no scoring during play. */
export function mugginsRuleRows(input: RuleRowsInput): readonly SettingsRow[] {
  const { settings: s, applyGameConfig, updateSettings, disableGameConfig } = input;
  if (isPenaltyVariant(s)) return [];
  return [
    {
      kind: 'toggle',
      key: 'mugginsStrict',
      label: t('settings.rows.mugginsStrict'),
      value: s.mugginsStrict,
      onClick: () => applyGameConfig({ mugginsStrict: !s.mugginsStrict }),
      disabled: disableGameConfig,
      tab: 'gameplay',
    },
    {
      kind: 'toggle',
      key: 'autoClaimMuggins',
      label: t('settings.rows.mugginsAutoClaim'),
      value: s.autoClaimMuggins,
      onClick: () => updateSettings({ autoClaimMuggins: !s.autoClaimMuggins }),
      tab: 'interface',
    },
  ];
}
